import { useState, useEffect } from 'react';
import { MessageCircle, X, Phone } from 'lucide-react';

interface WhatsAppButtonProps {
  phoneNumber?: string;
  message?: string;
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  showTooltip?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export default function WhatsAppButton({
  phoneNumber = "556182511308", // Número no formato internacional (Brasil: 55)
  message = "Olá! Gostaria de saber mais sobre o ChatVono.",
  position = 'bottom-right',
  showTooltip = true,
  size = 'md',
  className = ''
}: WhatsAppButtonProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [showMessage, setShowMessage] = useState(false);

  useEffect(() => {
    // Mostrar o botão após um pequeno delay para melhor UX
    const timer = setTimeout(() => setIsVisible(true), 1000);
    return () => clearTimeout(timer);
  }, []);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
    console.log('Opening WhatsApp URL:', whatsappUrl); // Para debug
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
  };

  const getPositionClasses = () => {
    switch (position) {
      case 'bottom-left':
        return 'bottom-6 left-6';
      case 'top-right':
        return 'top-6 right-6';
      case 'top-left':
        return 'top-6 left-6';
      default:
        return 'bottom-6 right-6';
    }
  };

  const getSizeClasses = () => {
    switch (size) {
      case 'sm':
        return 'w-12 h-12';
      case 'lg':
        return 'w-16 h-16';
      default:
        return 'w-14 h-14';
    }
  };

  const getIconSize = () => {
    switch (size) {
      case 'sm':
        return 'w-5 h-5';
      case 'lg':
        return 'w-7 h-7';
      default:
        return 'w-6 h-6';
    }
  };

  if (!isVisible) return null;

  return (
    <div className={`fixed z-50 ${getPositionClasses()} ${className}`}>
      {/* Tooltip/Message */}
      {showTooltip && showMessage && (
        <div className="absolute bottom-20 right-0 mb-2 max-w-sm animate-in fade-in slide-in-from-bottom-2 duration-300">
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 p-5 relative">
            <button
              onClick={() => setShowMessage(false)}
              className="absolute -top-2 -right-2 bg-gray-800 hover:bg-gray-700 text-white rounded-full w-6 h-6 flex items-center justify-center transition-colors shadow-lg"
              aria-label="Fechar mensagem"
            >
              <X className="w-3 h-3" />
            </button>
            
            <div className="flex items-start gap-3 mb-3">
              <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center flex-shrink-0 shadow-md">
                <MessageCircle className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-gray-900 text-base">ChatVono</p>
                <span className="inline-flex items-center text-xs text-green-600 mt-0.5">
                  <span className="w-2 h-2 bg-green-500 rounded-full mr-1 animate-pulse"></span>
                  Online agora
                </span>
              </div>
            </div>
            
            <div className="mb-4">
              <p className="text-gray-700 text-sm leading-relaxed">
                Vamos <strong>começar</strong> seu{' '}
                <strong className="text-green-600">Teste Grátis</strong> da plataforma? 😄
              </p>
            </div>

            <div className="flex items-center justify-between pt-3 border-t border-gray-100">
              <span className="text-xs text-gray-500 flex items-center gap-1">
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Resposta rápida
              </span>
            </div>

            {/* Arrow */}
            <div className="absolute -bottom-2 right-6 w-4 h-4 bg-white border-r border-b border-gray-100 transform rotate-45 shadow-sm"></div>
          </div>
        </div>
      )}

      {/* WhatsApp Button */}
      <div className="relative">
        <button
          onClick={handleClick}
          onMouseEnter={() => setShowMessage(true)}
          onMouseLeave={() => setShowMessage(false)}
          className={`
            ${getSizeClasses()}
            bg-gradient-to-br from-green-500 to-green-600 hover:from-green-600 hover:to-green-700
            text-white rounded-full shadow-lg hover:shadow-2xl 
            flex items-center justify-center 
            transition-all duration-300 ease-in-out
            transform hover:scale-110
            group
          `}
          aria-label="Entrar em contato via WhatsApp"
        >
          <MessageCircle className={`${getIconSize()} group-hover:scale-110 transition-transform`} />
        </button>
        
        {/* Ripple animation */}
        <div className="absolute inset-0 rounded-full bg-green-500 animate-ping opacity-20 pointer-events-none"></div>
        
        {/* Pulse animation */}
        <div className="absolute inset-0 rounded-full bg-green-400 opacity-30 animate-pulse pointer-events-none"></div>
      </div>
    </div>
  );
}